import { localStorage } from '@apparel/core/storage';

const STORAGE_KEY = 'signup_attribution';

export const ATTRIBUTION_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'gclid',
    'fbclid',
    'ttclid',
    'msclkid',
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type AttributionData = Partial<Record<AttributionKey, string>>;

/**
 * Captures and persists signup attribution (UTM / click IDs) from the URL.
 */
export class AttributionService {
    private static self: AttributionService;

    static get instance() {
        if (!this.self) {
            this.self = new AttributionService();
        }

        return this.self;
    }

    public static getInstance(): AttributionService {
        return this.instance;
    }

    /**
     * Capture UTM / click IDs from the current URL (first-touch wins).
     */
    public captureFromUrl(search?: string): AttributionData {
        if (typeof window === 'undefined') {
            return this.readStored();
        }

        const query = search ?? window.location.search;
        const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
        const stored = this.readStored();
        let changed = false;

        ATTRIBUTION_KEYS.forEach((key) => {
            const value = params.get(key);
            if (value && !stored[key]) {
                stored[key] = value;
                changed = true;
            }
        });

        if (changed) {
            this.writeStored(stored);
        }

        return stored;
    }

    /**
     * Capture from URL (if present) and return stored attribution.
     */
    public get(): AttributionData {
        this.captureFromUrl();
        return this.readStored();
    }

    /**
     * Attribution for signup payload.
     * Direct traffic (no UTM/click IDs): utm_source=direct, utm_medium=none.
     */
    public getForSignup(): AttributionData {
        const attribution = this.get();
        const hasAttribution = ATTRIBUTION_KEYS.some((key) => !!attribution[key]);

        if (hasAttribution) {
            return attribution;
        }

        return {
            utm_source: 'direct',
            utm_medium: 'none',
        };
    }

    public clear(): void {
        localStorage.removeItem(STORAGE_KEY);
    }

    public static captureFromUrl(search?: string): AttributionData {
        return this.instance.captureFromUrl(search);
    }

    public static get(): AttributionData {
        return this.instance.get();
    }

    public static getForSignup(): AttributionData {
        return this.instance.getForSignup();
    }

    public static clear(): void {
        this.instance.clear();
    }

    private readStored(): AttributionData {
        const stored = localStorage.getItem(STORAGE_KEY) || {};
        return typeof stored === 'object' && stored ? stored : {};
    }

    private writeStored(data: AttributionData): void {
        localStorage.setItem(STORAGE_KEY, data);
    }
}
